import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export interface EditModeState {
  isEditMode: boolean;

  userRole: 'internal' | 'external' | null;

  componentEditStates: Record<string, boolean>;

  activeTableEdits: Record<string, Set<string>>; // tableName -> set of edited row IDs

  settings: {
    autoSave: boolean;
    confirmOnExit: boolean;
    persistAcrossSessions: boolean;
  };
  editHistory: Array<{
    timestamp: number;
    action: string;
    tableName: string;
    recordId: string;
    changes: Record<string, unknown>;
  }>;
}

export interface EditModeActions {
  toggleEditMode: () => void;
  setEditMode: (enabled: boolean) => void;
  setUserRole: (role: 'internal' | 'external' | null) => void;

  setComponentEditMode: (componentId: string, enabled: boolean) => void;
  getComponentEditMode: (componentId: string) => boolean;
  addTableEdit: (tableName: string, recordId: string) => void;
  removeTableEdit: (tableName: string, recordId: string) => void;
  clearTableEdits: (tableName?: string) => void;
  hasTableEdits: (tableName: string) => boolean;
  getTableEditCount: (tableName: string) => number;
  addToHistory: (entry: Omit<EditModeState['editHistory'][0], 'timestamp'>) => void;
  clearHistory: () => void;
  updateSettings: (settings: Partial<EditModeState['settings']>) => void;

  resetEditMode: () => void;
  resetAllEdits: () => void;
}

export interface EditModeStore extends EditModeState, EditModeActions { }

const initialState: EditModeState = {
  isEditMode: true, // Start in edit mode for better UX
  userRole: null,
  componentEditStates: {},
  activeTableEdits: {},
  settings: {
    autoSave: false,
    confirmOnExit: true,
    persistAcrossSessions: true,
  },
  editHistory: [],
};

export const useEditModeStore = create<EditModeStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      toggleEditMode: () => {
        set((state) => ({
          isEditMode: !state.isEditMode,
        }));
      },

      setEditMode: (enabled: boolean) => {
        set({ isEditMode: enabled });
      },

      setUserRole: (role: 'internal' | 'external' | null) => {
        set({ userRole: role });
      },

      setComponentEditMode: (componentId: string, enabled: boolean) => {
        set((state) => ({
          componentEditStates: {
            ...state.componentEditStates,
            [componentId]: enabled,
          },
        }));
      },

      getComponentEditMode: (componentId: string) => {
        const state = get();
        return state.componentEditStates[componentId] ?? state.isEditMode;
      },

      addTableEdit: (tableName: string, recordId: string) => {
        set((state) => {
          const currentEdits = state.activeTableEdits[tableName] || new Set();
          currentEdits.add(recordId);

          return {
            activeTableEdits: {
              ...state.activeTableEdits,
              [tableName]: new Set(currentEdits),
            },
          };
        });
      },

      removeTableEdit: (tableName: string, recordId: string) => {
        set((state) => {
          const currentEdits = state.activeTableEdits[tableName] || new Set();
          currentEdits.delete(recordId);

          return {
            activeTableEdits: {
              ...state.activeTableEdits,
              [tableName]: new Set(currentEdits),
            },
          };
        });
      },

      clearTableEdits: (tableName?: string) => {
        set((state) => {
          if (tableName) {
            const { [tableName]: _, ...rest } = state.activeTableEdits;
            return { activeTableEdits: rest };
          } else {
            return { activeTableEdits: {} };
          }
        });
      },

      hasTableEdits: (tableName: string) => {
        const state = get();
        const edits = state.activeTableEdits[tableName];
        return edits && edits.size > 0;
      },

      getTableEditCount: (tableName: string) => {
        const state = get();
        const edits = state.activeTableEdits[tableName];
        return edits ? edits.size : 0;
      },

      addToHistory: (entry) => {
        set((state) => ({
          editHistory: [
            {
              ...entry,
              timestamp: Date.now(),
            },
            ...state.editHistory.slice(0, 99), // Keep last 100 entries
          ],
        }));
      },

      clearHistory: () => {
        set({ editHistory: [] });
      },

      updateSettings: (newSettings) => {
        set((state) => ({
          settings: {
            ...state.settings,
            ...newSettings,
          },
        }));
      },

      resetEditMode: () => {
        set({
          isEditMode: false,
          componentEditStates: {},
        });
      },

      resetAllEdits: () => {
        set({
          isEditMode: false,
          componentEditStates: {},
          activeTableEdits: {},
          editHistory: [],
        });
      },
    }),
    {
      name: 'edit-mode-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        isEditMode: state.isEditMode,
        userRole: state.userRole,
        settings: state.settings,
        // Don't persist activeTableEdits and editHistory as they are temporary
      }),
    }
  )
);

export const useIsEditMode = () => useEditModeStore((state) => state.isEditMode);
export const useUserRole = () => useEditModeStore((state) => state.userRole);
export const useEditModeSettings = () => useEditModeStore((state) => state.settings);
export const useTableEditCount = (tableName: string) =>
  useEditModeStore((state) => state.getTableEditCount(tableName));
export const useHasTableEdits = (tableName: string) =>
  useEditModeStore((state) => state.hasTableEdits(tableName));